import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { StoreKey } from "../models/cacheKey";
import { useFanInfo } from "../hooks/useFanInfo";
import { useCheckInDetail } from "../hooks/useCheckInDetail";
import { CheckInDetailHeader } from "../components/common/checkInDetailHeader";
import { useTranslation } from "react-i18next";
import { Loading } from "../components/common/Loading";
import { FanInfoTable } from "../components/fanInfo/fanInfoTable";
import { CheckInButton } from "../components/fanInfo/checkInButton";
import { Id, toast, ToastOptions } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import { useCheckInStatus } from "../hooks/useCheckInStatus";
import { CheckInInfoView } from "../components/fanInfo/checkInInfoView";
import { PenaltyActions } from "../components/fanInfo/penaltyActions";
import { PenaltyStatus } from "../models/penaltyStatus";
import { CheckInStatus } from "../models/enum/checkInStatus";
import { WinnerIdInfo } from "../models/winnerIdInfo";

export function FanInfoPageByTicket() {
  const [searchParams] = useSearchParams();
  const { artistId, eventId, rewardId } = useParams();
  const artistIdNum = Number(artistId) ?? 0;
  const eventIdNum = Number(eventId) ?? 0;
  const rewardIdNum = Number(rewardId) ?? 0;
  const ticketCode = searchParams.get(StoreKey.queryTicketCode);
  const {
    fanInfo,
    isLoading,
    refetch: refetchFanInfo,
  } = useFanInfo(artistIdNum, eventIdNum, rewardIdNum, { ticketCode });
  const { checkInDetail } = useCheckInDetail({
    artistId: artistIdNum,
    eventId: eventIdNum,
    rewardId: rewardIdNum,
  });
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { refetch } = useCheckInStatus(artistIdNum, eventIdNum, rewardIdNum);
  const [penaltyStatus, setPenaltyStatus] = useState<
    PenaltyStatus | undefined
  >();
  const toastId = useRef<Id>();

  useEffect(() => {
    if (fanInfo) {
      if (penaltyStatus) {
        const wasTardy = penaltyStatus.tardy;
        setPenaltyStatus({
          ...fanInfo?.penaltyStatus(),
          tardy: wasTardy,
        } as PenaltyStatus);
      } else {
        setPenaltyStatus(fanInfo?.penaltyStatus());
      }
    }
    //eslint-disable-next-line
  }, [fanInfo]);

  if (eventIdNum && rewardIdNum && ticketCode) {
    return (
      <div className={"flex flex-col h-full"}>
        <CheckInDetailHeader
          checkIn={checkInDetail}
          title={t("userdetail_title")}
        ></CheckInDetailHeader>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            {fanInfo && (
              <>
                <div className={"overflow-y-auto w-full"}>
                  <CheckInInfoView fanInfo={fanInfo} />
                  <FanInfoTable fanInfo={fanInfo} />
                  <PenaltyActions
                    penaltyStatus={penaltyStatus}
                    onChangeStatus={async (status) => {
                      if (
                        status.violation !== penaltyStatus?.violation ||
                        status.transfer !== penaltyStatus?.transfer
                      ) {
                        await refetchFanInfo();
                      }
                      setPenaltyStatus(status);
                    }}
                    artistId={artistIdNum}
                    eventId={eventIdNum}
                    rewardId={rewardIdNum}
                    ticketCode={fanInfo.ticketCode}
                    checkInStatus={fanInfo.ticketStatus}
                    fanIds={new WinnerIdInfo(fanInfo.fan.id, fanInfo.winnerId)}
                  />
                </div>
                <CheckInButton
                  onChange={async ({ isChecked, isLoading }) => {
                    if (isLoading) {
                      toastId.current = toast.loading(<Loading />, {
                        type: "info",
                      });
                    } else {
                      if (isChecked) {
                        const { data: refreshedFanInfo } =
                          await refetchFanInfo();
                        const { data: checkInStatus } = await refetch();
                        const toastText = t(
                          refreshedFanInfo?.isTardy()
                            ? "userdetail_flashmsg_latesubmit"
                            : "userdetail_flashmsg_submit",
                          {
                            current:
                              (checkInStatus?.regular.checkedIn || 0) +
                              (checkInStatus?.spare.checkedIn || 0),
                            total:
                              (checkInStatus?.regular.total || 0) +
                              (checkInStatus?.spare.total || 0),
                            ...(refreshedFanInfo?.isTardy() && {
                              n: refreshedFanInfo?.ticket.tardySequenceNumber,
                            }),
                          },
                        );
                        const toastOption = {
                          isLoading: false,
                          autoClose: 3000,
                          type: "success",
                          onClose: () => {
                            if (!refreshedFanInfo?.isTardy()) {
                              navigate(
                                pathname.substring(
                                  0,
                                  pathname.lastIndexOf("/"),
                                ) + "/scanner",
                                { replace: true },
                              );
                            }
                          },
                        } as ToastOptions;
                        if (toastId.current) {
                          toast.update(toastId.current, {
                            render: toastText,
                            ...toastOption,
                          });
                        } else {
                          toast.success(toastText, toastOption);
                        }
                      } else if (toastId.current) {
                        toast.dismiss(toastId.current);
                        await refetchFanInfo();
                      }
                      toastId.current = undefined;
                    }
                  }}
                  eventId={eventIdNum}
                  rewardId={rewardIdNum}
                  winnerId={fanInfo.winnerId}
                  ticketCode={fanInfo.ticketCode}
                  status={fanInfo?.ticketStatus}
                  isTardy={penaltyStatus?.tardy || false}
                  disabled={
                    checkInDetail?.checkInStatus !== CheckInStatus.IN_PROGRESS
                  }
                  artistId={artistIdNum}
                />
              </>
            )}
          </>
        )}
      </div>
    );
  } else {
    return <Navigate to={`/events/${eventIdNum}/rewards/${rewardIdNum}`} />;
  }
}
